/* eslint-disable @typescript-eslint/indent */
import { Postman, PostmanAcceptMethods } from '../types';

export type PostmanChache<T = any> = { [key: string]: T };
export type PostmanListener<T = any> = (payload: T) => void;
export type PostmanListenerObject<T = any> = {
  [key: string]: PostmanListener<T>[];
};

const postman: { target: Postman | null } = { target: null };

const chache = new Proxy<PostmanChache>(
  {},
  {
    set: (target, prop: string, value) => {
      // eslint-disable-next-line no-use-before-define
      if (listeners[prop]) {
        // eslint-disable-next-line no-use-before-define
        listeners[prop]?.forEach(listener => {
          listener(value);
        });
      }
      target[prop] = value;
      return true;
    },
  },
);

const listeners = new Proxy<PostmanListenerObject>(
  {},
  {
    set: (target, prop: string, value) => {
      if (value && chache[prop]) {
        value[value.length - 1](chache[prop]);
      }
      target[prop] = value;
      return true;
    },
  },
);

/**
 * @description Send data from the module
 * @param prefix module name
 */
function send<T = any>(prefix: string) {
  return (actionName: string, payload: T) => {
    const action = `${prefix}/${actionName}`;
    chache[action] = payload;
  };
}

/**
 * @description Get data from the module
 * @param action action name <module>/<name>
 * @param callback get data from callback
 * @retrun remove void
 */
function accept<T = any>(
  action: string,
  fn: PostmanListener<T>,
): {
  remove: () => void;
  once(): void;
  performance(callback: () => void, time?: number): void;
} {
  let isRun = false;
  let isOnce = false;

  const remove = (): void => {
    // eslint-disable-next-line no-use-before-define
    const index = listeners[action]?.findIndex(e => e === listener);
    listeners[action]?.splice(index ?? 0, 1);
  };

  const listener = (e: T): void => {
    fn(e);
    isRun = true;
    if (isOnce) {
      remove();
    }
  };

  if (!listeners[action]) {
    listeners[action] = [listener];
  } else {
    // @ts-expect-error iterator error
    listeners[action] = [...listeners[action], listener];
  }

  return {
    remove,
    once() {
      isOnce = true;
    },
    performance(callback: () => void, time = 5000) {
      setTimeout(() => {
        if (!isRun) {
          callback();
        }
      }, time);
    },
  };
}

/**
 * @description Async get data from the module
 * @param action action name <module>/<name>
 * @retrun promise
 */
const asyncAccept = <T = any>(
  action: string,
): {
  promise: Promise<T>;
  update(fn: (e: T) => void): void;
  remove: () => void;
  once: () => void;
  performance: (fn: () => void, timeout?: number | undefined) => void;
} => {
  let methods = {};
  let updateFn: (e: T) => void;
  const promise = new Promise<T>(resolve => {
    methods = accept(action, e => {
      resolve(e);
      if (updateFn) {
        updateFn(e);
      }
    });
  });

  return {
    ...(methods as PostmanAcceptMethods),
    promise,
    update(fn: (e: T) => void) {
      updateFn = fn;
    },
  };
};

/**
 * @description Get postman
 */
export function getPostman(): Postman | null {
  return postman.target;
}

export function initialPostman(name?: string): void {
  if (!getPostman()) {
    // eslint-disable-next-line no-use-before-define
    postman.target = makePostman(name ?? 'host');
  }
}

/**
 * @description Make postman instance
 * @param prefix module name string
 * @return {Postman} send, accept, watch
 */
export const makePostman = (prefix: string): Postman => {
  return {
    send: send(prefix),
    accept,
    asyncAccept,
  };
};
